/* NH collection filter drawer (snippets/nh-filter-drawer.liquid). Draft theme only.
   - Opens as a modal <dialog>: Esc closes, focus is trapped, focus returns to the opener.
   - Every change updates the "Show N results" count live through the Section Rendering
     API, without closing the drawer; the applied chips, header count and each facet's
     "picked" line follow the form state.
   - Submitting is a plain GET, so filter URLs, the back button and crawl stay native. */
(() => {
  if (window.__nhFilter) return;
  window.__nhFilter = true;
  const doc = document;
  const html = doc.documentElement;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const init = (dlg) => {
    if (dlg.__nhf) return;
    dlg.__nhf = true;
    const form = dlg.querySelector('[data-nhfd-form]');
    const body = dlg.querySelector('[data-nhfd-body]');
    const openers = [...doc.querySelectorAll(`[data-nhfd-open], [aria-controls="${dlg.id}"]`)];
    const applyBtn = dlg.querySelector('[data-nhfd-apply]');
    const applyText = dlg.querySelector('[data-nhfd-apply-text]');
    const live = dlg.querySelector('[data-nhfd-live]');
    const totalOut = dlg.querySelector('[data-nhfd-total]');
    const appliedBox = dlg.querySelector('[data-nhfd-applied]');
    const chipList = dlg.querySelector('[data-nhfd-chips]');
    const sectionId = dlg.dataset.sectionId;
    let lastOpener = null;

    const visible = (el) => !el.closest('[hidden]') && el.getClientRects().length > 0;
    const say = (msg) => { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); };

    /* ---------- Open / close ---------- */
    const open = (from) => {
      lastOpener = from || openers[0] || null;
      if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
      html.classList.add('nhfd-lock');
      for (const o of openers) o.setAttribute('aria-expanded', 'true');
      dlg.querySelector('.nhfd__title')?.focus({ preventScroll: true });
      dlg.dispatchEvent(new CustomEvent('nhfd:open'));
    };
    const close = () => {
      if (typeof dlg.close === 'function' && dlg.open) dlg.close(); else dlg.removeAttribute('open');
    };
    dlg.addEventListener('close', () => {
      html.classList.remove('nhfd-lock');
      dlg.style.removeProperty('--nhfd-drag');
      for (const o of openers) o.setAttribute('aria-expanded', 'false');
      if (!dlg.hasAttribute('data-submitting')) (lastOpener || openers[0])?.focus();
    });
    for (const o of openers) {
      o.setAttribute('aria-expanded', 'false');
      o.addEventListener('click', (e) => { e.preventDefault(); open(o); });
    }
    dlg.querySelector('[data-nhfd-close]')?.addEventListener('click', close);
    /* Backdrop tap: the click lands on the dialog box itself. */
    dlg.addEventListener('click', (e) => { if (e.target === dlg) close(); });
    /* Esc belongs to the dialog: keep the theme's global Esc handler (search drawer) from
       flashing its page overlay, which blocks taps for half a second. */
    for (const t of ['keydown', 'keyup']) dlg.addEventListener(t, (e) => { if (e.key === 'Escape') e.stopPropagation(); });
    /* Focus trap (showModal already makes the page inert; this keeps Tab cycling inside). */
    dlg.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const items = [...dlg.querySelectorAll(FOCUSABLE)].filter(visible);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (doc.activeElement === first || doc.activeElement === dlg.querySelector('.nhfd__title'))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* ---------- Drag the sheet down to dismiss (phones) ---------- */
    const head = dlg.querySelector('[data-nhfd-drag]');
    const sheetMq = window.matchMedia('(max-width: 749px)');
    let dragY = null;
    let dragDy = 0;
    head?.addEventListener('pointerdown', (e) => {
      if (!sheetMq.matches || e.target.closest('button')) return;
      dragY = e.clientY; dragDy = 0;
      head.setPointerCapture(e.pointerId);
      dlg.classList.add('is-dragging');
    });
    head?.addEventListener('pointermove', (e) => {
      if (dragY === null) return;
      dragDy = Math.max(0, e.clientY - dragY);
      dlg.style.setProperty('--nhfd-drag', `${dragDy}px`);
    });
    const endDrag = () => {
      if (dragY === null) return;
      dragY = null;
      dlg.classList.remove('is-dragging');
      if (dragDy > 110) close(); else dlg.style.removeProperty('--nhfd-drag');
    };
    head?.addEventListener('pointerup', endDrag);
    head?.addEventListener('pointercancel', endDrag);

    /* ---------- Accordions ---------- */
    for (const btn of dlg.querySelectorAll('[data-nhfd-toggle]')) {
      btn.addEventListener('click', () => {
        const panel = doc.getElementById(btn.getAttribute('aria-controls'));
        const on = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(on));
        if (panel) panel.hidden = !on;
      });
    }

    /* ---------- Long lists: top N, "Show all", search ---------- */
    const sets = [...dlg.querySelectorAll('[data-nhfd-set]')].map((set) => {
      const limit = Number(set.dataset.visible) || 6;
      const rows = [...set.querySelectorAll('[data-nhfd-opt]')];
      const more = set.querySelector('[data-nhfd-more]');
      const search = set.querySelector('[data-nhfd-search]');
      const searchWrap = set.querySelector('[data-nhfd-search-wrap]');
      const sub = set.querySelector('[data-nhfd-sub]');
      const none = set.querySelector('[data-nhfd-none]');
      const state = { expanded: false };
      const render = () => {
        const q = (search?.value || '').trim().toLowerCase();
        let shown = 0;
        rows.forEach((r, i) => {
          const checked = r.querySelector('input').checked;
          const keep = q ? r.dataset.label.includes(q) : (state.expanded || i < limit || checked);
          r.hidden = !keep;
          if (keep) shown += 1;
        });
        if (sub) {
          const firstAz = sub.nextElementSibling;
          sub.hidden = Boolean(q) || !(firstAz && !firstAz.hidden);
        }
        if (more) more.hidden = Boolean(q);
        if (none) {
          none.hidden = shown > 0;
          none.textContent = shown ? '' : `Nothing matches “${(search?.value || '').trim()}”`;
        }
      };
      more?.addEventListener('click', () => {
        state.expanded = !state.expanded;
        more.setAttribute('aria-expanded', String(state.expanded));
        more.textContent = state.expanded ? more.dataset.less : more.dataset.more;
        if (searchWrap && state.expanded) searchWrap.hidden = false;
        render();
        if (state.expanded) rows[limit]?.querySelector('input')?.focus({ preventScroll: false });
      });
      search?.addEventListener('input', render);
      search?.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
      render();
      return { set, render };
    });

    /* ---------- Price: quick bands + range inputs ---------- */
    const price = dlg.querySelector('[data-nhfd-price]');
    const pMin = price?.querySelector('[data-nhfd-min]');
    const pMax = price?.querySelector('[data-nhfd-max]');
    const bands = price ? [...price.querySelectorAll('[data-nhfd-band]')] : [];
    const cap = Number(price?.dataset.cap) || Infinity;
    const syncBands = () => {
      for (const b of bands) {
        const bMax = b.dataset.max === '' || Number(b.dataset.max) >= cap ? '' : b.dataset.max;
        b.setAttribute('aria-pressed', String(pMin.value === b.dataset.min && pMax.value === bMax));
      }
    };
    for (const b of bands) {
      b.addEventListener('click', () => {
        const on = b.getAttribute('aria-pressed') === 'true';
        pMin.value = on ? '' : b.dataset.min;
        pMax.value = on || b.dataset.max === '' || Number(b.dataset.max) >= cap ? '' : b.dataset.max;
        syncBands();
        changed();
      });
    }
    if (price) syncBands();

    /* ---------- Derived UI: chips, picked lines, header count ---------- */
    const money = (v) => `$${v}`;
    const priceLabel = () => {
      if (!pMin || (!pMin.value && !pMax.value)) return '';
      if (pMin.value && pMax.value) return `${money(pMin.value)}–${money(pMax.value)}`;
      if (pMax.value) return `Under ${money(pMax.value)}`;
      return `${money(pMin.value)}+`;
    };
    const checkedInputs = () => [...form.querySelectorAll('input[type="checkbox"]:checked')];
    const refreshUi = () => {
      const picked = checkedInputs();
      const pl = priceLabel();
      const n = picked.length + (pl ? 1 : 0);
      if (totalOut) {
        totalOut.hidden = n === 0;
        totalOut.textContent = n ? ` (${n})` : '';
      }
      for (const g of dlg.querySelectorAll('[data-nhfd-group]')) {
        const out = g.querySelector('[data-nhfd-picked]');
        if (!out) continue;
        let names;
        if (g.dataset.kind === 'price') names = pl ? [pl] : [];
        else names = [...g.querySelectorAll('input[type="checkbox"]:checked')].map((i) => i.dataset.label);
        out.textContent = names.length ? names.join(', ') : '';
        g.classList.toggle('has-picked', names.length > 0);
      }
      chipList.textContent = '';
      const addChip = (label, group, onRemove) => {
        const li = doc.createElement('li');
        const btn = doc.createElement('button');
        btn.type = 'button';
        btn.className = 'nhfd-applied';
        btn.setAttribute('aria-label', `Remove ${group}: ${label}`);
        btn.innerHTML = '<span class="nhfd-applied__t"></span><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M3 3l6 6M9 3L3 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
        btn.firstChild.textContent = label;
        btn.addEventListener('click', () => {
          const idx = [...chipList.querySelectorAll('.nhfd-applied')].indexOf(btn);
          onRemove();
          changed();
          say(`${label} removed`);
          const next = chipList.querySelectorAll('.nhfd-applied')[idx] || chipList.querySelectorAll('.nhfd-applied')[idx - 1] || chipList.querySelector('.nhfd-applied, .nhfd-applied-clear') || dlg.querySelector('.nhfd__title');
          next?.focus();
        });
        li.appendChild(btn);
        chipList.appendChild(li);
      };
      if (pl) addChip(pl, 'Price', () => { pMin.value = ''; pMax.value = ''; syncBands(); });
      for (const i of picked) addChip(i.dataset.label, i.dataset.group || 'Filter', () => { i.checked = false; });
      if (n > 0) {
        const li = doc.createElement('li');
        const clr = doc.createElement('button');
        clr.type = 'button';
        clr.className = 'nhfd-applied-clear';
        clr.textContent = 'Clear all';
        clr.addEventListener('click', () => { clearAll(); dlg.querySelector('.nhfd__title')?.focus(); });
        li.appendChild(clr);
        chipList.appendChild(li);
      }
      appliedBox.hidden = n === 0;
    };

    const clearAll = () => {
      for (const i of checkedInputs()) i.checked = false;
      if (pMin) { pMin.value = ''; pMax.value = ''; syncBands(); }
      for (const s of dlg.querySelectorAll('[data-nhfd-search]')) s.value = '';
      changed();
      say('All filters cleared');
    };
    dlg.querySelector('[data-nhfd-clear]')?.addEventListener('click', (e) => { e.preventDefault(); clearAll(); });

    /* ---------- Live count (Section Rendering API) ---------- */
    const params = () => {
      const sp = new URLSearchParams();
      for (const [k, v] of new FormData(form)) { if (v !== '' && k !== '') sp.append(k, v); }
      return sp;
    };
    const setCount = (n) => {
      applyBtn.dataset.count = String(n);
      applyBtn.disabled = n === 0;
      applyText.textContent = n === 0 ? 'No matches, adjust filters' : `Show ${n} result${n === 1 ? '' : 's'}`;
    };
    let timer = 0;
    let ctrl = null;
    const fetchCount = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        ctrl?.abort();
        ctrl = new AbortController();
        applyBtn.setAttribute('aria-busy', 'true');
        /* Small count-only section first; the full collection section is the fallback. */
        const load = async (id) => {
          const sp = params();
          sp.set('section_id', id);
          const res = await fetch(`${form.getAttribute('action')}?${sp}`, { signal: ctrl.signal, credentials: 'same-origin' });
          if (!res.ok) return null;
          const fresh = new DOMParser().parseFromString(await res.text(), 'text/html');
          return JSON.parse(fresh.querySelector('[data-nhfd-counts]')?.textContent || 'null');
        };
        try {
          const data = (await load('nh-filter-count')) || (await load(sectionId));
          if (!data) throw new Error('no counts');
          const n = Number(data.total);
          setCount(n);
          say(n === 0 ? 'No products match. Adjust filters.' : `${n} result${n === 1 ? '' : 's'}`);
          /* Refresh each option's count; dim options that would now give nothing. */
          const counts = new Map(data.values.map(([k, v, c]) => [`${k}\u0000${v}`, String(c)]));
          for (const i of form.querySelectorAll('input[type="checkbox"]')) {
            const opt = i.closest('[data-nhfd-opt]');
            const out = opt?.querySelector('[data-nhfd-n]');
            if (!out) continue;
            const c = counts.get(`${i.name}\u0000${i.value}`) ?? '0';
            out.textContent = c;
            opt.classList.toggle('is-zero', c === '0' && !i.checked);
          }
        } catch (err) {
          if (err.name !== 'AbortError') applyText.textContent = 'Show results';
        } finally {
          applyBtn.removeAttribute('aria-busy');
        }
      }, 220);
    };

    const changed = () => { refreshUi(); fetchCount(); };
    form.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('[data-nhfd-search]')) return;
      if (t === pMin || t === pMax) {
        /* Keep the range the right way round. */
        if (pMin.value && pMax.value && Number(pMin.value) > Number(pMax.value)) [pMin.value, pMax.value] = [pMax.value, pMin.value];
        syncBands();
      }
      changed();
    });
    /* Price typing: update chips/labels as they type, count once they pause. */
    for (const inp of [pMin, pMax]) inp?.addEventListener('input', () => {
      /* Plain text + inputmode=numeric (not type=number): a wheel over a focused number field changed the
         price instead of scrolling the drawer (owner, 2026-09-29). Digits only, capped at the range max. */
      const clean = inp.value.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');
      const capped = clean && Number(clean) > cap ? String(cap) : clean;
      if (capped !== inp.value) inp.value = capped;
      syncBands(); changed();
    });

    /* ---------- Submit: clean URL, keep the drawer honest ---------- */
    form.addEventListener('submit', () => {
      for (const i of form.querySelectorAll('[data-nhfd-min], [data-nhfd-max]')) if (i.value === '') i.disabled = true;
      for (const i of form.querySelectorAll('[data-nhfd-search]')) i.disabled = true;
      dlg.setAttribute('data-submitting', '');
      applyBtn.setAttribute('aria-busy', 'true');
    });
    /* Back button (bfcache): undo the submit-time changes. */
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      dlg.removeAttribute('data-submitting');
      applyBtn.removeAttribute('aria-busy');
      for (const i of form.querySelectorAll('input[disabled]')) i.disabled = false;
      close();
    });

    /* ---------- Scroll cues (owner, 2026-09-29: "can't scroll filters") ----------
       The body is the only scroller. While there is more above/below, the edge next to it gets a shadow and
       fade, and a "More filters" pill sits over the footer. A wheel over the header, chips or footer is
       handed to the body, so the drawer scrolls wherever the pointer is. */
    const cue = dlg.querySelector('[data-nhfd-morecue]');
    let cueRaf = 0;
    const edges = () => {
      cueRaf = 0;
      const max = body.scrollHeight - body.clientHeight;
      dlg.classList.toggle('nhfd--more-above', body.scrollTop > 4);
      dlg.classList.toggle('nhfd--more-below', max > 4 && body.scrollTop < max - 4);
    };
    const queueEdges = () => { if (!cueRaf) cueRaf = requestAnimationFrame(edges); };
    body.addEventListener('scroll', queueEdges, { passive: true });
    window.addEventListener('resize', queueEdges);
    if (window.ResizeObserver) { const ro = new ResizeObserver(queueEdges); ro.observe(body); for (const c of body.children) ro.observe(c); }
    dlg.addEventListener('click', () => setTimeout(queueEdges, 0));
    dlg.addEventListener('nhfd:open', queueEdges);
    cue?.addEventListener('click', () => {
      const behavior = reduce() ? 'auto' : 'smooth';
      const rest = body.scrollHeight - body.clientHeight - body.scrollTop;
      /* Little left to scroll (every other group is closed, as at 1050x800): open the next closed group
         and bring it up, so "More filters" always shows more filters. */
      const closed = rest < 160 && dlg.querySelector('[data-nhfd-toggle][aria-expanded="false"]');
      if (closed) {
        closed.click();
        requestAnimationFrame(() => body.scrollTo({ top: closed.closest('[data-nhfd-group]').offsetTop - 8, behavior }));
      } else body.scrollBy({ top: body.clientHeight * 0.7, behavior });
    });
    dlg.addEventListener('wheel', (e) => {
      if (e.ctrlKey || body.contains(e.target)) return;
      const max = body.scrollHeight - body.clientHeight;
      if (max <= 0) return;
      e.preventDefault();
      body.scrollBy({ top: e.deltaY * (e.deltaMode === 1 ? 16 : 1), behavior: 'instant' });
    }, { passive: false });
    /* A focused price field never keeps the wheel: the page-level default would change nothing now that
       the fields are text, but blur keeps older cached markup (type=number) safe too. */
    for (const inp of [pMin, pMax]) inp?.addEventListener('wheel', () => { if (doc.activeElement === inp && inp.type === 'number') inp.blur(); }, { passive: true });

    refreshUi();
    for (const s of sets) s.render();
    edges();
  };

  const boot = () => doc.querySelectorAll('[data-nhfd]').forEach(init);
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
  doc.addEventListener('shopify:section:load', boot);
})();
